export enum PermissionKey {
  PRODUCT_VIEW = 'PRODUCT_VIEW',
  PRODUCT_CREATE = 'PRODUCT_CREATE',
  PRODUCT_EDIT = 'PRODUCT_EDIT',
  PRODUCT_DELETE = 'PRODUCT_DELETE',
  ORDER_VIEW = 'ORDER_VIEW',
  ORDER_CREATE = 'ORDER_CREATE',
  ORDER_EDIT = 'ORDER_EDIT',
  ORDER_DELETE = 'ORDER_DELETE',
  CATEGORY_VIEW = 'CATEGORY_VIEW',
  CATEGORY_CREATE = 'CATEGORY_CREATE',
  CATEGORY_EDIT = 'CATEGORY_EDIT',
  CATEGORY_DELETE = 'CATEGORY_DELETE',
  INVENTORY_VIEW = 'INVENTORY_VIEW',
  INVENTORY_MANAGE = 'INVENTORY_MANAGE',
  INVENTORY_TRANSFER = 'INVENTORY_TRANSFER',
  INVENTORY_WRITEOFF = 'INVENTORY_WRITEOFF',
  INVENTORY_ADJUST = 'INVENTORY_ADJUST',
  DASHBOARD_VIEW = 'DASHBOARD_VIEW',
  REPORT_VIEW = 'REPORT_VIEW',
  STORE_VIEW = 'STORE_VIEW',
  ATTENDANCE_CLOCK = 'ATTENDANCE_CLOCK',
  ATTENDANCE_VIEW = 'ATTENDANCE_VIEW',
}

export type PermissionGroupKey =
  | 'Products'
  | 'Orders'
  | 'Categories'
  | 'Inventory'
  | 'Dashboard'
  | 'Reports'
  | 'Stores'
  | 'Attendance';

export type RoleTemplateKey = 'storeManager' | 'salesAssociate';

export interface PermissionDef {
  key: PermissionKey;
  group: PermissionGroupKey;
  labelKey: `PERMISSIONS.${PermissionKey}`;
}

export interface PermissionGroup {
  group: PermissionGroupKey;
  labelKey: `groups.${PermissionGroupKey}`;
  permissions: PermissionDef[];
}

export interface RoleTemplate {
  key: RoleTemplateKey;
  nameKey: `roles.${RoleTemplateKey}`;
  permissions: PermissionKey[];
}

const createPermission = (key: PermissionKey, group: PermissionGroupKey): PermissionDef => ({
  key,
  group,
  labelKey: `PERMISSIONS.${key}`,
});

export const CUSTOMER_PERMISSIONS: PermissionDef[] = [
  createPermission(PermissionKey.PRODUCT_VIEW, 'Products'),
  createPermission(PermissionKey.PRODUCT_CREATE, 'Products'),
  createPermission(PermissionKey.PRODUCT_EDIT, 'Products'),
  createPermission(PermissionKey.PRODUCT_DELETE, 'Products'),
  createPermission(PermissionKey.ORDER_VIEW, 'Orders'),
  createPermission(PermissionKey.ORDER_CREATE, 'Orders'),
  createPermission(PermissionKey.ORDER_EDIT, 'Orders'),
  createPermission(PermissionKey.ORDER_DELETE, 'Orders'),
  createPermission(PermissionKey.CATEGORY_VIEW, 'Categories'),
  createPermission(PermissionKey.CATEGORY_CREATE, 'Categories'),
  createPermission(PermissionKey.CATEGORY_EDIT, 'Categories'),
  createPermission(PermissionKey.CATEGORY_DELETE, 'Categories'),
  createPermission(PermissionKey.INVENTORY_VIEW, 'Inventory'),
  createPermission(PermissionKey.INVENTORY_MANAGE, 'Inventory'),
  createPermission(PermissionKey.INVENTORY_TRANSFER, 'Inventory'),
  createPermission(PermissionKey.INVENTORY_WRITEOFF, 'Inventory'),
  createPermission(PermissionKey.INVENTORY_ADJUST, 'Inventory'),
  createPermission(PermissionKey.DASHBOARD_VIEW, 'Dashboard'),
  createPermission(PermissionKey.REPORT_VIEW, 'Reports'),
  createPermission(PermissionKey.STORE_VIEW, 'Stores'),
  createPermission(PermissionKey.ATTENDANCE_CLOCK, 'Attendance'),
  createPermission(PermissionKey.ATTENDANCE_VIEW, 'Attendance'),
];

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    group: 'Products',
    labelKey: 'groups.Products',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Products'),
  },
  {
    group: 'Orders',
    labelKey: 'groups.Orders',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Orders'),
  },
  {
    group: 'Categories',
    labelKey: 'groups.Categories',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Categories'),
  },
  {
    group: 'Inventory',
    labelKey: 'groups.Inventory',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Inventory'),
  },
  {
    group: 'Dashboard',
    labelKey: 'groups.Dashboard',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Dashboard'),
  },
  {
    group: 'Reports',
    labelKey: 'groups.Reports',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Reports'),
  },
  {
    group: 'Stores',
    labelKey: 'groups.Stores',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Stores'),
  },
  {
    group: 'Attendance',
    labelKey: 'groups.Attendance',
    permissions: CUSTOMER_PERMISSIONS.filter((permission) => permission.group === 'Attendance'),
  },
];

export const ROLE_TEMPLATES: RoleTemplate[] = [
  {
    key: 'storeManager',
    nameKey: 'roles.storeManager',
    permissions: [
      PermissionKey.PRODUCT_VIEW,
      PermissionKey.PRODUCT_CREATE,
      PermissionKey.PRODUCT_EDIT,
      PermissionKey.PRODUCT_DELETE,
      PermissionKey.ORDER_VIEW,
      PermissionKey.ORDER_CREATE,
      PermissionKey.ORDER_EDIT,
      PermissionKey.ORDER_DELETE,
      PermissionKey.CATEGORY_VIEW,
      PermissionKey.CATEGORY_CREATE,
      PermissionKey.CATEGORY_EDIT,
      PermissionKey.CATEGORY_DELETE,
      PermissionKey.INVENTORY_VIEW,
      PermissionKey.INVENTORY_MANAGE,
      PermissionKey.INVENTORY_TRANSFER,
      PermissionKey.INVENTORY_WRITEOFF,
      PermissionKey.INVENTORY_ADJUST,
      PermissionKey.DASHBOARD_VIEW,
      PermissionKey.REPORT_VIEW,
      PermissionKey.STORE_VIEW,
      PermissionKey.ATTENDANCE_CLOCK,
      PermissionKey.ATTENDANCE_VIEW,
    ],
  },
  {
    key: 'salesAssociate',
    nameKey: 'roles.salesAssociate',
    permissions: [
      PermissionKey.PRODUCT_VIEW,
      PermissionKey.ORDER_VIEW,
      PermissionKey.ORDER_CREATE,
      PermissionKey.CATEGORY_VIEW,
      PermissionKey.INVENTORY_VIEW,
      PermissionKey.STORE_VIEW,
      PermissionKey.ATTENDANCE_CLOCK,
    ],
  },
];
